import { createApi } from '@reduxjs/toolkit/query/react'
import { hcPortalBaseQuery } from '@/features/baseQuery.ts'
import {
  OnboardingRecord,
  OnboardingRecordResponse,
  WorkableEmployeeSearchOption,
} from '@/features/onboarding/onboarding.def.ts'
import {
  CompleteOnboardingRecordRequest,
  CreateOnboardingChecklistRequest,
  CreateOnboardingProbationRecordRequest,
  DiscardOnboardingRecordRequest,
  DownloadWorkableDocumentRequest,
  EditOnboardingRecordRequest,
  GetOnboardingRecordRequest,
  InitOnboardingRecordRequest,
  ListOnboardingRecordsRequest,
  PatchOnboardingRecordRequest,
  SearchWorkableEmployeesRequest,
} from '@/features/onboarding/onboarding-request.def.ts'
import { HcFile } from '@/common/types/personal-info.def.ts'
import { HcError } from '@/common/types/api.def.ts'
import { downloadFile } from '@/utils/misc.ts'
import {
  DownloadEmployeeDocumentRequest,
  UploadEmployeeDocumentRequest,
} from '@/features/employee-data/employee-data-request.def.ts'

export const onboardingApi = createApi({
  reducerPath: 'onboarding',
  baseQuery: hcPortalBaseQuery(),
  tagTypes: ['OnboardingRecord'],
  endpoints: (builder) => ({
    listOnboardingRecords: builder.query<OnboardingRecord[], ListOnboardingRecordsRequest>({
      query: (request) => ({
        url: 'Onboarding/ListOnboardingRecords',
        params: request,
      }),
      providesTags: () => [{ type: 'OnboardingRecord', id: 'LIST' }],
    }),
    searchWorkableEmployees: builder.query<WorkableEmployeeSearchOption[], SearchWorkableEmployeesRequest>({
      query: (request) => ({
        url: 'Onboarding/SearchWorkableEmployees',
        params: request,
      }),
    }),
    getOnboardingRecord: builder.query<OnboardingRecordResponse, GetOnboardingRecordRequest>({
      query: (request) => ({
        url: 'Onboarding/GetOnboardingRecord',
        params: request,
      }),
      providesTags: (_, __, { id }) => [{ type: 'OnboardingRecord', id }],
    }),
    initOnboardingRecord: builder.mutation<OnboardingRecord, InitOnboardingRecordRequest>({
      query: (request) => ({
        url: 'Onboarding/InitOnboardingRecord',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: [{ type: 'OnboardingRecord', id: 'LIST' }],
    }),
    editOnboardingRecord: builder.mutation<boolean, EditOnboardingRecordRequest>({
      query: (request) => ({
        url: 'Onboarding/EditOnboardingRecord',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'OnboardingRecord', id: 'LIST' },
        { type: 'OnboardingRecord', id },
      ],
    }),
    patchOnboardingRecord: builder.mutation<boolean, PatchOnboardingRecordRequest>({
      query: (request) => ({
        url: 'Onboarding/PatchOnboardingRecord',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'OnboardingRecord', id },
      ],
    }),
    discardOnboardingRecord: builder.mutation<boolean, DiscardOnboardingRecordRequest>({
      query: (request) => ({
        url: 'Onboarding/DiscardOnboardingRecord',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'OnboardingRecord', id: 'LIST' },
        { type: 'OnboardingRecord', id },
      ],
    }),
    completeOnboardingRecord: builder.mutation<boolean, CompleteOnboardingRecordRequest>({
      query: (request) => ({
        url: 'Onboarding/CompleteOnboardingRecord',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'OnboardingRecord', id: 'LIST' },
        { type: 'OnboardingRecord', id },
      ],
    }),
    createOnboardingChecklist: builder.mutation<number, CreateOnboardingChecklistRequest>({
      query: (request) => ({
        url: 'Onboarding/CreateOnboardingChecklist',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'OnboardingRecord', id },
      ],
    }),
    createOnboardingProbationRecord: builder.mutation<number, CreateOnboardingProbationRecordRequest>({
      query: (request) => ({
        url: 'Onboarding/CreateOnboardingProbationRecord',
        method: 'POST',
        body: request,
      }),
      invalidatesTags: (_, __, { id }) => [
        { type: 'OnboardingRecord', id },
      ],
    }),
    uploadOnboardingDocument: builder.mutation<HcFile, UploadEmployeeDocumentRequest>({
      query: (request) => {
        const formData = new FormData()
        formData.append('onboardingRecordId', request.employeeId)
        formData.append('document', request.document)
        formData.append('type', request.type)

        return {
          url: '/OnboardingDocument/UploadOnboardingDocument',
          method: 'POST',
          cache: 'no-cache',
          body: formData,
        }
      },
    }),
    downloadOnboardingDocument: builder.mutation<void, DownloadEmployeeDocumentRequest>({
      query: (request) => {
        return {
          url: '/OnboardingDocument/DownloadOnboardingDocument',
          method: 'GET',
          cache: 'no-cache',
          params: request,
          responseHandler: async (response) => {
            if (!response.ok) {
              return await response.json() as HcError
            }
            const blob = await response.blob()
            downloadFile(request.fileName, blob)
          },
        }
      },
    }),
    downloadWorkableDocument: builder.mutation<void, DownloadWorkableDocumentRequest>({
      queryFn: async (request) => {
        try {
          const response = await fetch(request.url)
          if (!response.ok) {
            return {
              error: {
                status: response.status,
                data: {
                  errorCode: 'DownloadWorkableDocumentFailed',
                  hcErrorCode: response.status,
                  message: 'Failed to download workable file. Please try again later.',
                } satisfies HcError,
              },
            }
          }

          const blob = await response.blob()
          downloadFile(request.fileName, blob)
          return { data: undefined }
        } catch {
          return {
            error: {
              status: 0,
              data: {
                errorCode: 'DownloadWorkableDocumentFailed',
                hcErrorCode: 0,
                message: 'Failed to download workable file. Please try again later.',
              } satisfies HcError,
            },
          }
        }
      },
    }),
  }),
})

export const {
  useListOnboardingRecordsQuery,
  useLazySearchWorkableEmployeesQuery,
  useGetOnboardingRecordQuery,
  useInitOnboardingRecordMutation,
  useEditOnboardingRecordMutation,
  usePatchOnboardingRecordMutation,
  useDiscardOnboardingRecordMutation,
  useCompleteOnboardingRecordMutation,
  useUploadOnboardingDocumentMutation,
  useDownloadOnboardingDocumentMutation,
  useDownloadWorkableDocumentMutation,
  useCreateOnboardingChecklistMutation,
  useCreateOnboardingProbationRecordMutation,
} = onboardingApi
